import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import * as authApi from "../api/authApi";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [initializing, setInitializing] = useState(true);
  // Mirrors the backend switch PHONE_OTP_REQUIRED, so the wording follows it. Off until the backend says otherwise.
  const [phoneOtpRequired, setPhoneOtpRequired] = useState(false);
  const stopRefreshRef = useRef(null);

  const startAutoRefresh = () => {
    if (stopRefreshRef.current) stopRefreshRef.current();
    stopRefreshRef.current = authApi.scheduleTokenRefresh(
      (newToken, newUser) => {
        setAccessToken(newToken);
        if (newUser) setUser(newUser);
      }
    );
  };

  const stopAutoRefresh = () => {
    if (stopRefreshRef.current) {
      stopRefreshRef.current();
      stopRefreshRef.current = null;
    }
  };

  useEffect(() => {
    authApi
      .getAuthConfig()
      .then(({ ok, data }) => setPhoneOtpRequired(Boolean(ok && data?.data?.phoneOtpRequired)))
      .catch(() => {
        // Backend unreachable: keep the default (off); the session check below copes the same way.
      });
  }, []);

  // On first load there's no access token in memory yet (by design - it's
  // never persisted to localStorage), so silently try to mint a new one from
  // the HttpOnly refresh cookie the browser already holds, if any.
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const { ok, data } = await authApi.refreshAccessToken();
        if (ok && data?.data?.token) {
          setAccessToken(data.data.token);
          setUser(data.data.user || null);
          startAutoRefresh();
        }
      } catch {
        // Backend unreachable (e.g. not running locally) - treat as no session.
      } finally {
        setInitializing(false);
      }
    };

    restoreSession();

    return () => stopAutoRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startSession = (payload) => {
    setAccessToken(payload.token);
    setUser(payload.user || null);
    startAutoRefresh();
  };

  // Customers whose phone isn't verified yet get no session from login/signup -
  // they get `{ challenge }` and must finish with sendOtp() / verifyOtp().
  // Everyone else (and admins) get `{ user }` and are signed in.
  const login = async (email, password) => {
    const { ok, data } = await authApi.login(email, password);
    if (ok && data?.data?.requiresPhoneVerification) return { challenge: data.data };
    if (!ok || !data?.data?.token) {
      throw new Error(data?.message || "Invalid email or password.");
    }
    startSession(data.data);
    return { user: data.data.user };
  };

  const signup = async (payload) => {
    const { ok, data } = await authApi.signup(payload);
    if (ok && data?.data?.requiresPhoneVerification) return { challenge: data.data };
    if (!ok || !data?.data?.token) {
      throw new Error(data?.message || "Could not create your account.");
    }
    startSession(data.data);
    return { user: data.data.user };
  };

  // Returns { phoneMasked, resendInSeconds, devOtp? }; `devOtp` only exists when
  // the backend runs the console SMS driver outside production.
  const sendOtp = async (challengeToken, phone) => {
    const { ok, data } = await authApi.sendOtp(challengeToken, phone);
    if (!ok) {
      const error = new Error(data?.message || "Could not send the OTP.");
      error.retryAfter = data?.errors?.retryAfter;
      throw error;
    }
    return data.data;
  };

  const verifyOtp = async (challengeToken, code, phone) => {
    const { ok, data } = await authApi.verifyOtp(challengeToken, code, phone);
    if (!ok || !data?.data?.token) {
      throw new Error(data?.message || "Could not verify the OTP.");
    }
    startSession(data.data);
    return data.data.user;
  };

  const logout = async () => {
    stopAutoRefresh();
    try {
      await authApi.logout();
    } catch {
      // best-effort - clear local state regardless
    }
    setAccessToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isLoggedIn: Boolean(accessToken),
        initializing,
        phoneOtpRequired,
        login,
        signup,
        sendOtp,
        verifyOtp,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
